/**
 * FolderRow · 列表视图下单条文件夹行
 *
 * 视觉：横向一行 · 小图标 + 名称 + 张数 + 子文件夹数 + 最后更新时间
 *      行尾默认显示状态徽标（添加中 / 未匹配）；hover 时切换为操作按钮组
 *      （下载 / 重命名 / 删除）
 *
 * 列表视图只展示顶层文件夹，不支持行内展开子级（含子文件夹的行
 * 点击进入详情页查看），因此不渲染展开 chevron 列。
 *
 * 多选模式：
 *  · selectMode=true 时最左侧渲染勾选框，整行点击改成切换勾选
 *  · 多选态下 hover 操作按钮不渲染（避免误触；批量操作放底部）
 *
 * 权限：
 *  · canManage=false 时隐藏 hover 操作组里的「重命名 / 删除」（只剩下载）
 *    与右键菜单的 isAdmin 守门保持一致
 */

import React from 'react'
import classNames from 'classnames'
import { CheckOutlined, LoadingOutlined } from '@ant-design/icons'
import type { FolderListItem } from '../../../../types/ai-classify'
import FolderIconSvg from './FolderIconSvg'
import styles from './FolderRow.module.scss'

interface FolderRowProps {
  folder: FolderListItem
  /** 整行点击 · 普通态跳详情；selectMode 切换勾选（FolderList 内分支） */
  onClick: (folderId: string) => void
  /** AI 归档任务进行中 · 行尾显示"添加中..."徽标 */
  archiving?: boolean
  /** 归档超时（扫描结束未匹配到照片） · 行尾灰色徽标，优先级低于 archiving */
  archivingTimedOut?: boolean
  /** 多选模式 · 最左侧渲染勾选框；整行点击改成切换勾选 */
  selectMode?: boolean
  selected?: boolean
  /** hover 操作按钮 · 与右键菜单同语义，未传则不渲染 */
  onDownload?: (folder: FolderListItem) => void
  onRename?: (folder: FolderListItem) => void
  onDelete?: (folder: FolderListItem) => void
  /** 是否允许「重命名 / 删除」 · 与右键菜单 isAdmin 守门一致 · 默认 true */
  canManage?: boolean
  /** 右键菜单 · antd Dropdown 通过 cloneElement 注入 · 根元素必须接住 */
  onContextMenu?: (e: React.MouseEvent<HTMLDivElement>) => void
  /** checkbox 单独点击 · 同 FolderCard.onSelectClick */
  onSelectClick?: (folderId: string) => void
  /**
   * HTML5 拖拽 · 与 FolderCard 同款语义；列表行也支持拖动到目标行
   * 不传 = 不可拖
   */
  draggable?: boolean
  onDragStart?: (
    e: React.DragEvent<HTMLDivElement>,
    folder: FolderListItem
  ) => void
  onDragEnd?: (
    e: React.DragEvent<HTMLDivElement>,
    folder: FolderListItem
  ) => void
  onDragOver?: (
    e: React.DragEvent<HTMLDivElement>,
    folder: FolderListItem
  ) => void
  onDragLeave?: (
    e: React.DragEvent<HTMLDivElement>,
    folder: FolderListItem
  ) => void
  onDrop?: (e: React.DragEvent<HTMLDivElement>, folder: FolderListItem) => void
  dragging?: boolean
  dragOver?: boolean
}

/** 千分位 · en-US 锁死避免环境差异 */
function formatPhotoCount(count: number): string {
  return count.toLocaleString('en-US')
}

/** 时间戳 → 简短日期；缺字段返 "—" 让 UI 不空着 */
function formatUpdateTime(ts: number): string {
  if (!ts || ts <= 0) return '—'
  const d = new Date(ts)
  const now = new Date()
  const sameYear = d.getFullYear() === now.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  const hh = String(d.getHours()).padStart(2, '0')
  const mm = String(d.getMinutes()).padStart(2, '0')
  return sameYear
    ? `${m}-${day} ${hh}:${mm}`
    : `${d.getFullYear()}-${m}-${day} ${hh}:${mm}`
}

const FolderRow = React.forwardRef<HTMLDivElement, FolderRowProps>(
  (
    {
      folder,
      onClick,
      archiving,
      archivingTimedOut,
      selectMode,
      selected,
      onDownload,
      onRename,
      onDelete,
      canManage = true,
      onContextMenu,
      onSelectClick,
      draggable,
      onDragStart,
      onDragEnd,
      onDragOver,
      onDragLeave,
      onDrop,
      dragging,
      dragOver
    },
    ref
  ) => {
    // 虚拟项（待归档）· 银灰配色 + 禁止选中：不渲染勾选框、不挂 data-folder-id
    const isVirtual = folder.virtual === true
    // 智能分类文件夹不再用蓝色区分，而是黄色底 + 右下角刷新水印（isAi 传给图标）
    const isAi = folder.aiGenerated === true
    const variant: 'manual' | 'gray' = isVirtual ? 'gray' : 'manual'
    const showHasChildrenIcon = folder.subfolderCount > 0
    /** 框选 / 空白菜单识别属性 · 见 FolderCard 同名说明 */
    const itemAttrs = isVirtual
      ? { 'data-no-box-select': true }
      : { 'data-folder-id': folder.folderId }

    /** hover 操作按钮 · 阻止冒泡避免触发整行点击 */
    const handleAction = (
      e: React.MouseEvent,
      fn: ((folder: FolderListItem) => void) | undefined
    ): void => {
      e.stopPropagation()
      if (fn) fn(folder)
    }

    return (
      <div
        ref={ref}
        className={classNames(styles.row, {
          [styles.rowSelectable]: selectMode,
          [styles.rowSelected]: selectMode && selected,
          [styles.rowDragging]: dragging,
          [styles.rowDragOver]: dragOver
        })}
        onClick={() => onClick(folder.folderId)}
        onContextMenu={onContextMenu}
        draggable={draggable}
        onDragStart={onDragStart ? (e) => onDragStart(e, folder) : undefined}
        onDragEnd={onDragEnd ? (e) => onDragEnd(e, folder) : undefined}
        onDragOver={onDragOver ? (e) => onDragOver(e, folder) : undefined}
        onDragLeave={onDragLeave ? (e) => onDragLeave(e, folder) : undefined}
        onDrop={onDrop ? (e) => onDrop(e, folder) : undefined}
        role="button"
        aria-label={
          selectMode
            ? `${selected ? '取消勾选' : '勾选'} ${folder.folderName}`
            : `打开文件夹 ${folder.folderName}`
        }
        aria-pressed={selectMode ? selected : undefined}
        {...itemAttrs}
      >
        {/* 勾选框 · 总是渲染（占列宽固定 18）
          · selectMode：常亮可见
          · 非 selectMode：默认透明，hover 行时淡入
          · 单独 onClick → onSelectClick；非多选态点击进 manageMode + 选中本行
          · 虚拟项（待归档）不支持选中 → 换成等宽空占位，保证图标列与其他行对齐 */}
        {isVirtual ? (
          <span className={styles.selectPlaceholder} aria-hidden />
        ) : (
          <span
            className={classNames(styles.selectIndicator, {
              [styles.selectIndicatorChecked]: selected,
              [styles.selectIndicatorAlways]: selectMode
            })}
            onClick={
              onSelectClick
                ? (e) => {
                    e.stopPropagation()
                    onSelectClick(folder.folderId)
                  }
                : undefined
            }
            role="checkbox"
            aria-checked={!!selected}
            aria-label={selected ? '取消勾选' : '勾选'}
          >
            {selected && (
              <CheckOutlined style={{ fontSize: 12, color: '#fff' }} />
            )}
          </span>
        )}
        <div className={styles.iconWrap}>
          <FolderIconSvg
            size={36}
            variant={variant}
            isAi={isAi}
            hasChildren={showHasChildrenIcon}
          />
        </div>
        <div className={styles.name} title={folder.folderName}>
          {folder.folderName}
        </div>
        {/* 张数列
          · 归档中：显示小型紫色"添加中"徽标（列表视图无明确"右上角"，徽标就在张数槽位）
          · 归档完成 / 超时：照常显示数字（包括 0 张），不再有"未匹配"特殊徽标
          · archivingTimedOut prop 已无视觉副作用，保留接口供将来加 tooltip 等 */}
        <div className={styles.metaPhoto}>
          {archiving ? (
            <span className={styles.archivingBadge} aria-label="照片添加中">
              <LoadingOutlined style={{ marginRight: 4, fontSize: 11 }} />
              添加中
            </span>
          ) : (
            `${formatPhotoCount(folder.photoCount)} 张`
          )}
        </div>
        <div className={styles.metaSubfolder}>
          {folder.subfolderCount > 0
            ? `${folder.subfolderCount} 个子文件夹`
            : '—'}
        </div>
        <div className={styles.metaCtime} title="创建时间">
          {formatUpdateTime(folder.ctime)}
        </div>
        <div className={styles.metaTime} title="最后更新时间">
          {formatUpdateTime(folder.updateTime)}
        </div>
        {/**
         * 行尾 · hover 操作按钮（下载 / 重命名 / 删除） · 文本按钮无 icon
         *  · 多选态下也展示 hover 按钮（用户可以对单行做单项操作，不强制走批量）
         *  · archiving / timeout 状态徽标已上移到张数列覆盖不准确的张数
         *  · 保留 statusBadgeLayer 空 span 让 .endSlot 高度稳定（hover 切按钮组）
         */}
        <div className={styles.endSlot}>
          <span className={styles.statusBadgeLayer}>
            {/* 状态徽标已移走 */}
          </span>
          <span
            className={styles.hoverActions}
            // 容器自身不响应点击；交给内部按钮（不影响整行 onClick / 整行 toggle 选中）
            onClick={(e) => e.stopPropagation()}
          >
            {onDownload && (
              <button
                type="button"
                className={styles.actionBtn}
                onClick={(e) => handleAction(e, onDownload)}
                aria-label="下载"
              >
                下载
              </button>
            )}
            {canManage && onRename && (
              <button
                type="button"
                className={styles.actionBtn}
                onClick={(e) => handleAction(e, onRename)}
                aria-label="重命名"
              >
                重命名
              </button>
            )}
            {canManage && onDelete && (
              <button
                type="button"
                className={classNames(styles.actionBtn, styles.actionBtnDanger)}
                onClick={(e) => handleAction(e, onDelete)}
                aria-label="删除"
              >
                删除
              </button>
            )}
          </span>
        </div>
      </div>
    )
  }
)

FolderRow.displayName = 'FolderRow'

// React.memo · 框选实时高亮时父组件每帧重渲染，列表行靠 props 浅比较跳过未变动的行
// （父级传的回调均 useCallback 稳定、folder 引用稳定，只有选中翻转的行才重渲染）
export default React.memo(FolderRow)
